import { useGetAllEmployeesQuery } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import {
  BONUS_PAY_BASIS_OPTIONS,
  useCreateNewBonusPolicyMutation,
  useGetSalaryStructureQuery,
  useGetSingleBonusPolicyQuery,
  useUpdateBonusPolicyMutation,
  type BonusPayBasis,
  type BonusPolicyBody,
  type BonusPolicyDetail,
} from '@/app/features/employee/emp-payroll';
import CustomDrawer from '@/components/CustomDrawer';
import EmployeeSelect, {
  type EmployeeMinimal,
} from '@/components/EmployeeSelect';
import WithLoader from '@/components/WithLoader';
import { ChevronDownIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { snakeToCamel } from '@/utils/string.utils';
import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Switch,
  type FormInstance,
} from 'antd';
import { useState } from 'react';
import { EMPLOYEE_TYPE_OPTIONS } from '../shared/payroll.constants';
import {
  flattenAssigned,
  groupUserIdsByDepartment,
  toAmount,
} from '../shared/payroll.helpers';

export type BonusPolicyMode = 'ADD' | 'UPDATE' | 'VIEW';

type Props = {
  open: boolean;
  mode: BonusPolicyMode;
  /** The policy to view or edit; absent when creating one. */
  id?: string;
  handleClose: () => void;
};

type FormValues = {
  title: string;
  status: boolean;
  type: 'flat' | 'percentage';
  amountFlat?: number;
  amountPercentage?: number;
  amountPercentageOf?: string;
  countPerFiscalYear: number;
  /** Empty only on a policy saved before pay basis existed. */
  payBasis?: BonusPayBasis;
  employeeTypes: string[];
  assigned_employees: Record<string, string[]>;
};

const TITLES: Record<BonusPolicyMode, string> = {
  ADD: 'New Bonus Policy',
  UPDATE: 'Edit Bonus Policy',
  VIEW: 'View Bonus Policy',
};

/**
 * Create, edit or view a bonus policy: its amount (flat or a percentage of a
 * salary component), how many times a fiscal year it is paid, and who gets it.
 */
const ManageBonus = ({ open, mode, id, handleClose }: Props) => (
  <CustomDrawer
    width={800}
    open={open}
    onClose={handleClose}
    title={TITLES[mode]}
  >
    {/* Mounted only while open, so every policy starts from its own values. */}
    {open && <BonusPolicyForm mode={mode} id={id} onDone={handleClose} />}
  </CustomDrawer>
);

/** Loads the policy (when there is one) before the form is shown. */
const BonusPolicyForm = ({
  mode,
  id,
  onDone,
}: {
  mode: BonusPolicyMode;
  id?: string;
  onDone: () => void;
}) => {
  const [form] = Form.useForm<FormValues>();
  const isView = mode === 'VIEW';

  const { data: policyData, isLoading: policyLoading } =
    useGetSingleBonusPolicyQuery({ id: id as string }, { skip: !id });
  const policy = id ? policyData?.data : undefined;

  // The employee list follows the chosen employee types and pay basis. Before
  // the form is on screen, the saved policy's values are used.
  const watchedTypes = Form.useWatch('employeeTypes', form);
  const employeeTypes = watchedTypes ?? policy?.employee_types ?? [];
  const watchedBasis = Form.useWatch('payBasis', form);
  const payBasis = watchedBasis ?? policy?.pay_basis ?? undefined;
  const {
    data: employeeData,
    currentData: currentEmployees,
    isFetching: employeesLoading,
  } = useGetAllEmployeesQuery({
    params: {
      skip: 0,
      limit: 0,
      status: 'active',
      employee_types: employeeTypes,
      // "All" (or no basis) lists everyone, as before.
      ...(payBasis === 'MONTHLY' || payBasis === 'DAILY'
        ? { pay_basis: payBasis }
        : {}),
    },
  });
  const employees = (employeeData?.data?.data || []) as EmployeeMinimal[];

  // An existing policy needs its data and the employees of its own types
  // (`currentData`, not a list left over from other types) before the form
  // can be filled; a new one can show right away. Once shown, the form stays,
  // even while a change of employee type refetches the list.
  const [isReady, setIsReady] = useState(!id);
  if (!isReady && policy && currentEmployees) setIsReady(true);

  return (
    <WithLoader
      isLoading={!isReady && (policyLoading || employeesLoading)}
      className="rounded-lg min-h-[300px]"
    >
      {isReady && (
        <PolicyFields
          form={form}
          isView={isView}
          id={id}
          employees={employees}
          initialValues={toFormValues(policy, employees)}
          onDone={onDone}
        />
      )}
    </WithLoader>
  );
};

/** Saved policy → form values. A new policy starts flat, active and for all. */
const toFormValues = (
  policy: BonusPolicyDetail | undefined,
  employees: EmployeeMinimal[],
): Partial<FormValues> => {
  if (!policy) {
    return {
      type: 'flat',
      status: true,
      payBasis: 'ALL',
      assigned_employees: {},
    };
  }
  const isPercentage = toAmount(policy.amount_percentage) > 0;
  return {
    title: policy.title,
    status: policy.status === 'active',
    type: isPercentage ? 'percentage' : 'flat',
    amountFlat: toAmount(policy.amount_flat),
    amountPercentage: toAmount(policy.amount_percentage),
    amountPercentageOf: policy.amount_percentage_of || undefined,
    countPerFiscalYear: policy.count_per_fiscal_year,
    payBasis: policy.pay_basis || undefined,
    employeeTypes: policy.employee_types,
    // The picker keeps ids grouped by department.
    assigned_employees: groupUserIdsByDepartment(policy.user_ids, employees),
  };
};

const PolicyFields = ({
  form,
  isView,
  id,
  employees,
  initialValues,
  onDone,
}: {
  form: FormInstance<FormValues>;
  isView: boolean;
  id?: string;
  employees: EmployeeMinimal[];
  initialValues: Partial<FormValues>;
  onDone: () => void;
}) => {
  const amountType = Form.useWatch('type', form) ?? initialValues.type;
  const assignedEmployees = Form.useWatch('assigned_employees', form);

  const { data: structureData } = useGetSalaryStructureQuery(null);
  const [createPolicy, { isLoading: creating }] =
    useCreateNewBonusPolicyMutation();
  const [updatePolicy, { isLoading: updating }] =
    useUpdateBonusPolicyMutation();

  // A percentage is taken from one structure component or the gross salary.
  const percentageOfOptions = [
    ...(structureData?.data?.salary_structures || []).map((item) => ({
      value: item.component_name,
      label: snakeToCamel(item.component_name),
    })),
    { value: 'gross', label: 'Gross Salary' },
  ];

  const handleSubmit = async (values: FormValues) => {
    const isPercentage = values.type === 'percentage';
    if (isPercentage && !values.amountPercentageOf) {
      form.setFields([
        { name: 'amountPercentage', errors: ['Please select percentage of'] },
      ]);
      return;
    }

    const body: BonusPolicyBody = {
      title: values.title,
      status: values.status ? 'active' : 'inactive',
      employeeTypes: values.employeeTypes,
      countPerFiscalYear: values.countPerFiscalYear,
      // Left out when unset, so a legacy policy keeps the server's default.
      payBasis: values.payBasis,
      amountFlat: isPercentage ? 0 : toAmount(values.amountFlat),
      amountPercentage: isPercentage ? toAmount(values.amountPercentage) : 0,
      amountPercentageOf: isPercentage
        ? values.amountPercentageOf || null
        : null,
      userIds: flattenAssigned(values.assigned_employees),
    };

    try {
      if (id) {
        await updatePolicy({ id, body }).unwrap();
        toast('success', 'Bonus policy updated successfully');
      } else {
        await createPolicy(body).unwrap();
        toast('success', 'Bonus policy created successfully');
      }
      onDone();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to save bonus policy'));
    }
  };

  return (
    <Form
      form={form}
      size="large"
      layout="vertical"
      disabled={isView}
      initialValues={initialValues}
      className="custom-form space-y-6"
      onFinish={isView ? undefined : handleSubmit}
    >
      <Form.Item noStyle name="assigned_employees" />

      <div className="grid grid-cols-2 gap-6">
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: !isView, message: 'Please enter title' }]}
        >
          <Input placeholder="e.g. Festival Bonus" />
        </Form.Item>

        {/*
          Amount: a flat BDT figure, or a percentage of a salary component.
          A real form label, stretched full width (`label-full`) so the Flat / %
          switch sits at its right; this keeps the box level with Title's.
          The mark is drawn by hand because the automatic one lands after the
          switch.
        */}
        <Form.Item
          required={false}
          className="label-full"
          label={
            <Flex
              justify="space-between"
              align="center"
              className="w-full h-[22px]"
            >
              <span>
                Amount
                {!isView && (
                  <span className="ml-1 text-[14px] leading-none text-theme-error-500">
                    *
                  </span>
                )}
              </span>
              <Form.Item name="type" noStyle>
                <Radio.Group
                  className="flex benefit-radio-btn"
                  onChange={() =>
                    form.resetFields([
                      'amountFlat',
                      'amountPercentage',
                      'amountPercentageOf',
                    ])
                  }
                >
                  <Radio.Button value="flat">Flat</Radio.Button>
                  <Radio.Button value="percentage">%</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Flex>
          }
        >
          {amountType === 'flat' ? (
            <Form.Item
              name="amountFlat"
              className="!mb-0"
              rules={[{ required: !isView, message: 'Please enter amount' }]}
            >
              <InputNumber
                min={1}
                prefix="৳"
                precision={2}
                controls={false}
                placeholder="0.00"
                className="w-full"
              />
            </Form.Item>
          ) : (
            <Space.Compact className="w-full">
              <Form.Item
                name="amountPercentage"
                className="!mb-0 flex-1"
                rules={[
                  { required: !isView, message: 'Please enter percentage' },
                ]}
              >
                <InputNumber
                  min={1}
                  max={100}
                  suffix="%"
                  precision={2}
                  controls={false}
                  placeholder="0 %"
                  className="border !border-theme-slate-700 rounded-md !border-r-0 w-full"
                />
              </Form.Item>
              <Form.Item
                name="amountPercentageOf"
                className="!mb-0 border border-theme-slate-700 rounded-md pr-1 rounded-l-none !border-l-0 h-[46px]"
              >
                <Select
                  size="small"
                  placeholder="Of"
                  placement="bottomRight"
                  className="!w-[160px] compact-select"
                  options={percentageOfOptions}
                  onChange={() =>
                    form.setFields([{ name: 'amountPercentage', errors: [] }])
                  }
                />
              </Form.Item>
            </Space.Compact>
          )}
        </Form.Item>
      </div>

      <Form.Item
        label="Status"
        name="status"
        layout="horizontal"
        valuePropName="checked"
        className="border-b border-t !mb-0 !py-0.5 border-b-theme-gray-700 border-t-theme-gray-700 horizontal-end"
      >
        <Switch className="custom-switch" />
      </Form.Item>

      <div className="grid grid-cols-3 gap-6">
        <Form.Item label="Pay Basis" name="payBasis">
          <Select
            placeholder="Not set"
            options={BONUS_PAY_BASIS_OPTIONS}
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            // Another population, another employee list: start the pick over.
            onChange={() => form.setFieldValue('assigned_employees', {})}
          />
        </Form.Item>
        <Form.Item
          label="Employee Type"
          name="employeeTypes"
          rules={[
            { required: !isView, message: 'Please select employee type' },
          ]}
        >
          <Select
            mode="multiple"
            maxTagCount="responsive"
            placeholder="Select"
            options={EMPLOYEE_TYPE_OPTIONS}
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
          />
        </Form.Item>
        <Form.Item
          label="Times Per Fiscal Year"
          name="countPerFiscalYear"
          rules={[
            {
              required: !isView,
              message: 'Please enter times per fiscal year',
            },
          ]}
        >
          <InputNumber
            min={1}
            precision={0}
            controls={false}
            placeholder="e.g. 2"
            className="w-full"
          />
        </Form.Item>
      </div>

      <EmployeeSelect form={form} employees={employees} required={!isView} />
      <EmployeeSelect.Selected
        form={form}
        isView={isView}
        employees={employees}
        assignedEmployees={assignedEmployees || {}}
      />

      {!isView && (
        <Flex justify="end">
          <Button
            htmlType="submit"
            loading={creating || updating}
            className="btn-primary min-w-[80px] h-11 font-semibold"
          >
            {id ? 'Update Policy' : 'Create Policy'}
          </Button>
        </Flex>
      )}
    </Form>
  );
};

export default ManageBonus;
